import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

const CONDITION_LABELS = {
  nm: 'Near Mint',
  lp: 'Lightly Played',
  mp: 'Moderately Played',
  hp: 'Heavily Played',
  dmg: 'Damaged',
};

const CONDITION_ORDER = ['nm', 'lp', 'mp', 'hp', 'dmg'];

const CONDITION_COLORS = {
  nm: '#22c55e',
  lp: '#84cc16',
  mp: '#f59e0b',
  hp: '#f97316',
  dmg: '#ef4444',
};

// Color gradient for graded tiers (highest grade = green, lowest = red)
function getGradeColor(index, total) {
  const colors = ['#22c55e', '#4ade80', '#84cc16', '#a3e635', '#facc15', '#f59e0b', '#f97316', '#ef4444'];
  const colorIdx = Math.min(Math.floor((index / Math.max(total - 1, 1)) * (colors.length - 1)), colors.length - 1);
  return colors[colorIdx];
}

/**
 * Normalize pricing data from either old format (conditions/gradedTiers)
 * or new Countdown format (stats from card_prices table).
 * Returns an object with .conditions and .gradedTiers that the UI components expect.
 */
function normalizePricingData(pricing) {
  if (!pricing) return pricing;

  // Already has old format
  if (pricing.conditions || pricing.gradedTiers) return pricing;

  // New Countdown format: stats object from card_prices table
  const stats = pricing.stats;
  if (!stats) return pricing;

  const isGraded = pricing.is_graded === true;

  if (isGraded) {
    // Convert stats keys like "PSA 10" -> gradedTiers format
    const gradedTiers = {};
    for (const [key, data] of Object.entries(stats)) {
      if (key === 'all' || key === 'raw' || key === 'unknown') continue;
      gradedTiers[key] = {
        price: data.med ?? data.avg ?? null,
        low: data.low ?? null,
        high: data.high ?? null,
        saleCount: data.sales ?? 0,
      };
    }
    return { ...pricing, isGraded: true, gradedTiers };
  }

  // Raw: convert stats keys like "nm", "lp", "all" -> conditions format
  const conditions = {};
  for (const [key, data] of Object.entries(stats)) {
    if (key === 'all' || key === 'raw' || key === 'unknown') continue;
    conditions[key] = {
      price: data.med ?? data.avg ?? null,
      low: data.low ?? null,
      high: data.high ?? null,
      sales: data.sales ?? 0,
    };
  }

  // If no condition-specific data but we have an "all" bucket, use it as NM
  if (Object.keys(conditions).length === 0 && stats.all) {
    conditions.nm = {
      price: stats.all.med ?? stats.all.avg ?? null,
      low: stats.all.low ?? null,
      high: stats.all.high ?? null,
      sales: stats.all.sales ?? 0,
    };
  }

  return { ...pricing, conditions };
}

const TradePricingOverlay = ({ card, onClose, onConditionSelect, cardDbId }) => {
  const { theme } = useTheme();
  const [activeCondition, setActiveCondition] = useState(card?.condition?.toLowerCase() || 'nm');

  // Sync activeCondition when card changes
  useEffect(() => {
    if (card?.condition) {
      setActiveCondition(card.condition.toLowerCase());
    }
  }, [card?.condition]);

  if (!card) return null;

  // Normalize pricing data: support both old format (conditions/gradedTiers)
  // and new Countdown format (stats from card_prices table)
  const rawPricing = card.pricingData;
  const pricing = normalizePricingData(rawPricing);
  const isGraded = pricing?.isGraded === true || pricing?.is_graded === true;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: theme.overlay,
          zIndex: 500,
          display: 'flex',
          alignItems: 'flex-end',
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          style={{
            width: '100%',
            maxHeight: '85vh',
            backgroundColor: theme.bgCard,
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            overflowY: 'auto',
            paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Handle bar */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: theme.border }} />
          </div>

          {/* Card header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 20px 16px' }}>
            {(card.userPhoto || card.digitalImage) && (
              <img
                src={card.digitalImage || card.userPhoto}
                alt=""
                style={{ width: 52, height: 73, borderRadius: 6, objectFit: 'cover' }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: theme.text, fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {card.name}
              </div>
              <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 2 }}>
                {card.setName} {card.number ? `#${card.number}` : ''}
              </div>
              {card.condition && (
                <span style={{
                  display: 'inline-block',
                  marginTop: 4,
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor: theme.accentBg,
                  color: theme.accent,
                }}>
                  {card.condition}
                </span>
              )}
            </div>
          </div>

          {isGraded ? (
            <GradedLadder
              pricing={pricing}
              card={card}
              theme={theme}
              activeCondition={activeCondition}
              setActiveCondition={setActiveCondition}
              onConditionSelect={onConditionSelect}
            />
          ) : (
            <RawLadder
              pricing={pricing}
              theme={theme}
              activeCondition={activeCondition}
              setActiveCondition={setActiveCondition}
              onConditionSelect={onConditionSelect}
            />
          )}

          {/* Source attribution */}
          <div style={{ padding: '4px 20px 0', textAlign: 'center' }}>
            <span style={{ fontSize: 11, color: theme.textMuted }}>
              via eBay Sold Data
            </span>
          </div>

          {/* Chef's Kitchen link */}
          {(cardDbId || card.cardDbId) && (
            <div style={{ padding: '8px 20px 4px', textAlign: 'center' }}>
              <span
                style={{
                  fontSize: 12, fontWeight: 600, color: theme.accent,
                  cursor: 'pointer', textDecoration: 'underline',
                }}
                onClick={() => {
                  onClose();
                  window.location.href = `/card/${cardDbId || card.cardDbId}`;
                }}
              >
                View full pricing in Chef's Kitchen
              </span>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Raw condition ladder (NM, LP, MP, HP, DMG) -- JustTCG data
const RawLadder = ({ pricing, theme, activeCondition, setActiveCondition, onConditionSelect }) => {
  const conditions = pricing?.conditions || {};

  const allPrices = CONDITION_ORDER
    .map(k => conditions[k]?.price)
    .filter(p => p != null && p > 0);
  const maxPrice = allPrices.length > 0 ? Math.max(...allPrices) : 1;

  const trend7d = pricing?.trend?.d7;
  const trend30d = pricing?.trend?.d30;
  const allTimeLow = pricing?.allTime?.low;
  const allTimeHigh = pricing?.allTime?.high;
  const rangePosition = pricing?.allTime?.position;
  const currentPrice = conditions.nm?.price || conditions.lp?.price || allPrices[0];

  return (
    <>
      {/* Condition Ladder */}
      <div style={{ padding: '0 20px 16px' }}>
        <div style={{ color: theme.textSecondary, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
          Condition Pricing
        </div>
        {CONDITION_ORDER.map(key => {
          const data = conditions[key];
          if (!data?.price) return null;
          const barWidth = (data.price / maxPrice) * 100;
          const isActive = activeCondition === key;
          const isInteractive = !!onConditionSelect;

          const rowContent = (
            <>
              <span style={{
                width: 36,
                fontSize: 12,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? CONDITION_COLORS[key] : theme.textSecondary,
                textTransform: 'uppercase',
                flexShrink: 0,
              }}>
                {key}
              </span>
              <div style={{ flex: 1, height: 20, backgroundColor: theme.bg, borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                <div style={{
                  width: `${barWidth}%`,
                  height: '100%',
                  backgroundColor: CONDITION_COLORS[key],
                  borderRadius: 4,
                  opacity: isActive ? 0.9 : 0.4,
                  transition: 'width 0.3s ease',
                }} />
                {isActive && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${barWidth}%`,
                    borderRadius: 4,
                    border: `2px solid ${CONDITION_COLORS[key]}`,
                    boxSizing: 'border-box',
                  }} />
                )}
              </div>
              <span style={{
                width: 60,
                textAlign: 'right',
                fontSize: 13,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? theme.text : theme.textSecondary,
                flexShrink: 0,
              }}>
                ${data.price.toFixed(2)}
              </span>
            </>
          );

          if (isInteractive) {
            return (
              <button
                key={key}
                onClick={() => {
                  setActiveCondition(key);
                  onConditionSelect(key);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 8,
                  opacity: isActive ? 1 : 0.6,
                  background: 'none',
                  border: 'none',
                  padding: '4px 0',
                  width: '100%',
                  cursor: 'pointer',
                }}
              >
                {rowContent}
              </button>
            );
          }

          return (
            <div key={key} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 8,
              opacity: isActive ? 1 : 0.6,
            }}>
              {rowContent}
            </div>
          );
        })}
      </div>

      {/* Trend Chips */}
      {(trend7d != null || trend30d != null) && (
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ color: theme.textSecondary, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            Price Trends
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {trend7d != null && (
              <TrendChip label="7d" value={trend7d} theme={theme} />
            )}
            {trend30d != null && (
              <TrendChip label="30d" value={trend30d} theme={theme} />
            )}
          </div>
        </div>
      )}

      {/* All-Time Range Bar */}
      {allTimeLow != null && allTimeHigh != null && allTimeHigh > allTimeLow && (
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ color: theme.textSecondary, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            All-Time Range
          </div>
          <div style={{ position: 'relative', height: 28, marginBottom: 4 }}>
            <div style={{
              position: 'absolute',
              top: 8,
              left: 0,
              right: 0,
              height: 12,
              borderRadius: 6,
              background: `linear-gradient(to right, ${theme.error}, ${theme.warning}, ${theme.success})`,
              opacity: 0.3,
            }} />
            {rangePosition != null && (
              <div style={{
                position: 'absolute',
                top: 4,
                left: `${Math.min(Math.max(rangePosition * 100, 2), 98)}%`,
                transform: 'translateX(-50%)',
                width: 20,
                height: 20,
                borderRadius: 10,
                backgroundColor: theme.accent,
                border: `3px solid ${theme.bgCard}`,
                boxShadow: `0 0 0 1px ${theme.accent}`,
              }} />
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: theme.textSecondary }}>
              Low: ${allTimeLow.toFixed(2)}
            </span>
            {currentPrice != null && (
              <span style={{ fontSize: 12, color: theme.accent, fontWeight: 700 }}>
                Now: ${currentPrice.toFixed(2)}
              </span>
            )}
            <span style={{ fontSize: 11, color: theme.textSecondary }}>
              High: ${allTimeHigh.toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

// Graded tier ladder (PSA 10, PSA 9, ...) -- Poketrace data
const GradedLadder = ({ pricing, card, theme, activeCondition, setActiveCondition, onConditionSelect }) => {
  const gradedTiers = pricing?.gradedTiers || {};
  const tierEntries = Object.entries(gradedTiers);

  if (tierEntries.length === 0) {
    return (
      <div style={{ padding: '0 20px 16px', textAlign: 'center' }}>
        <span style={{ color: theme.textSecondary, fontSize: 13 }}>No graded pricing available</span>
      </div>
    );
  }

  const allPrices = tierEntries.map(([, d]) => d.price).filter(p => p != null && p > 0);
  const maxPrice = allPrices.length > 0 ? Math.max(...allPrices) : 1;
  const isInteractive = !!onConditionSelect;

  return (
    <div style={{ padding: '0 20px 16px' }}>
      <div style={{ color: theme.textSecondary, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
        {pricing.company} Graded Pricing
      </div>
      {tierEntries.map(([label, data], idx) => {
        if (!data?.price) return null;
        const barWidth = (data.price / maxPrice) * 100;
        const isActive = activeCondition === label.toLowerCase();
        const color = getGradeColor(idx, tierEntries.length);

        const rowContent = (
          <>
            <span style={{
              width: 52,
              fontSize: 11,
              fontWeight: isActive ? 700 : 500,
              color: isActive ? color : theme.textSecondary,
              flexShrink: 0,
            }}>
              {label}
            </span>
            <div style={{ flex: 1, height: 20, backgroundColor: theme.bg, borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
              <div style={{
                width: `${barWidth}%`,
                height: '100%',
                backgroundColor: color,
                borderRadius: 4,
                opacity: isActive ? 0.9 : 0.4,
                transition: 'width 0.3s ease',
              }} />
              {isActive && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${barWidth}%`,
                  borderRadius: 4,
                  border: `2px solid ${color}`,
                  boxSizing: 'border-box',
                }} />
              )}
            </div>
            <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 70 }}>
              <span style={{
                fontSize: 13,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? theme.text : theme.textSecondary,
              }}>
                ${data.price.toFixed(2)}
              </span>
              {data.saleCount > 0 && (
                <div style={{ fontSize: 9, color: theme.textMuted, marginTop: 1 }}>
                  {data.saleCount} sold
                </div>
              )}
            </div>
          </>
        );

        if (isInteractive) {
          return (
            <button
              key={label}
              onClick={() => {
                setActiveCondition(label.toLowerCase());
                onConditionSelect(label);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 8,
                opacity: isActive ? 1 : 0.6,
                background: 'none',
                border: 'none',
                padding: '4px 0',
                width: '100%',
                cursor: 'pointer',
              }}
            >
              {rowContent}
            </button>
          );
        }

        return (
          <div key={label} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 8,
            opacity: isActive ? 1 : 0.6,
          }}>
            {rowContent}
          </div>
        );
      })}
    </div>
  );
};

const TrendChip = ({ label, value, theme }) => {
  const isUp = value > 0;
  const isFlat = value === 0;
  const color = isFlat ? theme.textSecondary : isUp ? theme.success : theme.error;
  const bg = isFlat ? theme.bg : isUp ? theme.successBg : theme.errorBg;
  const arrow = isFlat ? '' : isUp ? '\u2191' : '\u2193';

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '8px 14px',
      borderRadius: 8,
      backgroundColor: bg,
      border: `1px solid ${color}`,
    }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: theme.textSecondary }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color }}>
        {arrow} {Math.abs(value).toFixed(1)}%
      </span>
    </div>
  );
};

export default TradePricingOverlay;
